import React, { useState } from "react";
import { callGemini } from "../../services/geminiService";

const QuestionCard = ({
  question,
  onAnswer,
  isAnswered,
  selectedAnswer,
  showFeedback,
}) => {
  const [showAIExplanation, setShowAIExplanation] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");
  const [isLoadingAI, setIsLoadingAI] = useState(false);

  const handleSelectOption = (idx) => {
    if (!showFeedback || !isAnswered) {
      onAnswer(idx);
    }
  };

  const handleGetAIExplanation = async () => {
    setIsLoadingAI(true);
    setShowAIExplanation(true);

    const explanation = await callGemini(
      `Explica brevemente por qué la respuesta correcta a la pregunta "${
        question.q
      }" es "${question.o[question.c]}". Cita leyes de Panamá si es posible.`
    );

    setAiExplanation(explanation);
    setIsLoadingAI(false);
  };

  return (
    <div className="bg-white rounded-[2rem] md:rounded-[3rem] p-6 md:p-10 border border-slate-100 shadow-2xl animate-fade">
      {/* SECCIÓN PREGUNTA: Justificado y centrado en móvil, alineado a la izquierda en escritorio */}
      <h3 className="text-lg md:text-xl font-bold text-slate-900 leading-relaxed mb-6 md:mb-12 text-center md:text-left md:text-justify tracking-normal">
        {question.q}
      </h3>

      <div className="grid gap-3 md:gap-4">
        {question.o.map((option, idx) => {
          const isCorrect = idx === question.c;
          const isSelected = idx === selectedAnswer;

          const visualCorrect = showFeedback && isAnswered && isCorrect;
          const visualIncorrect =
            showFeedback && isAnswered && isSelected && !isCorrect;

          const visualSimulacroSelected =
            !showFeedback && isAnswered && isSelected;

          const letter = String.fromCharCode(65 + idx);

          return (
            <button
              key={idx}
              onClick={() => handleSelectOption(idx)}
              disabled={isAnswered && showFeedback}
              className={`
                w-full text-left p-4 md:p-6 rounded-2xl border-2 font-bold text-slate-700 
                transition-all relative overflow-hidden group
                ${
                  (!isAnswered || !showFeedback) &&
                  "hover:border-blue-300 hover:bg-blue-50/50 hover:shadow-md"
                }
                ${
                  visualCorrect
                    ? "border-emerald-500 bg-gradient-to-r from-emerald-50 to-green-50"
                    : ""
                }
                ${
                  visualIncorrect
                    ? "border-rose-500 bg-gradient-to-r from-rose-50 to-red-50"
                    : ""
                }
                ${
                  visualSimulacroSelected
                    ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                    : ""
                }
                ${
                  isAnswered && !isSelected && (!visualCorrect || !showFeedback)
                    ? "border-slate-100 bg-slate-50/50 opacity-60"
                    : ""
                }
              `}
            >
              <div className="flex items-center gap-3 md:gap-4">
                <span
                  className={`
                    w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-xl text-xs md:text-sm font-bold transition-all shrink-0
                    ${
                      !isAnswered || (!showFeedback && !isSelected)
                        ? "bg-slate-100 text-slate-600 border-2 border-slate-200"
                        : ""
                    }
                    ${
                      visualCorrect
                        ? "bg-emerald-500 text-white border-2 border-emerald-600"
                        : ""
                    }
                    ${
                      visualIncorrect
                        ? "bg-rose-500 text-white border-2 border-rose-600"
                        : ""
                    }
                    ${
                      visualSimulacroSelected
                        ? "bg-blue-600 text-white border-2 border-blue-700"
                        : ""
                    }
                  `}
                >
                  {letter}
                </span>
                <span className="flex-grow text-sm md:text-base leading-snug">
                  {option}
                </span>

                {visualCorrect && (
                  <span className="w-7 h-7 md:w-8 md:h-8 bg-emerald-500 rounded-xl flex items-center justify-center text-white shrink-0">
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                )}
                {visualIncorrect && (
                  <span className="w-7 h-7 md:w-8 md:h-8 bg-rose-500 rounded-xl flex items-center justify-center text-white shrink-0">
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {isAnswered && showFeedback && (
        <div className="mt-6 md:mt-10 p-5 md:p-8 rounded-[2rem] border-2 bg-white animate-fade shadow-lg">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            <div
              className={`w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center text-white shrink-0 ${
                selectedAnswer === question.c
                  ? "bg-gradient-to-br from-emerald-500 to-green-600"
                  : "bg-gradient-to-br from-rose-500 to-red-600"
              }`}
            >
              {selectedAnswer === question.c ? (
                <svg
                  className="w-6 h-6 md:w-8 md:h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 md:w-8 md:h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>
            <div className="flex-grow">
              <p className="text-xl md:text-2xl font-black mb-2 md:mb-3">
                {selectedAnswer === question.c ? "¡Correcto!" : "Incorrecto"}
              </p>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-200">
                {question.exp}
              </p>
              {/*{" "}
              {showAIExplanation ? (
                <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t-2 border-indigo-100">
                  <p className="text-[10px] md:text-xs font-black text-indigo-600 uppercase mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 md:w-6 md:h-6 bg-indigo-100 rounded-lg flex items-center justify-center">
                      ✨
                    </span>
                    Explicación de la IA
                  </p>
                  <div className="text-xs md:text-sm text-slate-700 leading-relaxed bg-gradient-to-r from-indigo-50 to-purple-50 p-4 md:p-5 rounded-xl border border-indigo-100">
                    {isLoadingAI ? "Analizando..." : aiExplanation}
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleGetAIExplanation}
                  disabled={isLoadingAI}
                  className="mt-4 md:mt-6 flex items-center gap-2 text-xs md:text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors bg-indigo-50 px-4 md:px-5 py-2 md:py-3 rounded-xl hover:bg-indigo-100"
                >
                  <span className="w-5 h-5 md:w-6 md:h-6 bg-indigo-200 rounded-lg flex items-center justify-center">
                    ✨
                  </span>
                  Pedir explicación a la IA
                </button>
              )}{" "}
             */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
